import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { User } from 'app/auth/models/User';
import { AuthenticationStore } from 'app/permission/services/authentication.store';
import { UserLocalStorageService } from 'app/permission/services/user.local.storage.service';
import { MultiAgency } from 'app/shared/models/multi-agency';
import { MultiAgencyService } from 'app/shared/multi-agency/multi-agency.service';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from '../../menus/service/menu.store';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { LogService } from '../../shared/log/log.service';
import { OnyxEvent } from '../models/event';
import { RatePlanCode } from '../models/rate-plan-code';
import { EventSearchParametersStore } from '../service/event-search-parameters.store';
import { EventStore } from '../service/event.store';
import { EventCollisionModalComponent } from 'app/shared/event-collision-modal/event-collision-modal.component';
import { EventCollisionModalConfig } from 'app/shared/event-collision-modal/event-collision-modal-config';
import { EventCollisionMatch } from '../models/event-collision-match';
import { MatchingPropertyStore } from '../service/matching-property.store';
import { ChainBrandStore } from '../service/chainbrand/chain-brand.store';
import { EventCollisionInfo } from '../models/event-collision-info';
import { ReservationStore } from '../service/reservation-store';

@Component({
  selector: 'pioneer-add-event',
  templateUrl: './add-event.component.html',
})
export class AddEventComponent implements OnInit {
  public eventForm: FormGroup;

  public onyxEvent: OnyxEvent;

  public isReadOnly: boolean;

  private user: User;

  public clientContactEditable = true;

  public propertyEmail: string;

  public agencyEmail: string;

  public modelInCollision: any;

  evenInfo: EventCollisionInfo;

  eventMatchId: number;

  @ViewChild(EventCollisionModalComponent)
  eventCollisionModal: EventCollisionModalComponent;
  public eventCollisionModalConfig = new EventCollisionModalConfig();

  constructor(
    public router: Router,
    private formBuilder: FormBuilder,
    private eventStore: EventStore,
    private reservationStore: ReservationStore,
    private logService: LogService,
    private menuStore: MenuStore,
    private flashMessagesService: FlashMessagesService,
    private eventSearchParametersStore: EventSearchParametersStore,
    private multiAgencyService: MultiAgencyService,
    private authenticationStore: AuthenticationStore,
    private userLocalStorageService: UserLocalStorageService,
    private matchingPropertyStore: MatchingPropertyStore
  ) {}

  ngOnInit() {
    this.isReadOnly = false;

    this.authenticationStore.user.subscribe((user: User) => {
      this.user = user;
    });

    this.onyxEvent = new OnyxEvent();
    this.eventForm = this.formBuilder.group({
      event: this.formBuilder.group({}),
    });
    this.menuStore.setPageHeader('EVENT.TITLE_ADD', false);

    this.multiAgencyService.setEventSelected(true);

    this.authenticationStore.clientContactEditable$.subscribe((value) => {
      this.clientContactEditable = value;
    });
  }

  save(model: any): Observable<OnyxEvent> {
    this.eventForm.disable();

    model.event.eventAgency = model.eventAgency;
    model.event.eventClient = model.eventClient;
    model.event.eventProperty = model.eventProperty;

    const formModel = new OnyxEvent(model.event);
    const updatedRefCodes: Object = {};

    if (formModel.eventAgency) {
      updatedRefCodes['agencyId'] = formModel.eventAgency.agencyId;

      if (this.authenticationStore.isAgencyUser()) {
        formModel.eventAgency.eventReferenceNumber =
          model.event.groupCodeNumber;
      }
    }

    if (formModel.eventClient) {
      updatedRefCodes['clientReferenceNumber'] =
        formModel.eventClient.clientReferenceNumber;
    }

    if (formModel.eventProperty) {
      if (this.authenticationStore.isPropertyUser()) {
        formModel.eventProperty.eventReferenceNumber =
          model.event.groupCodeNumber;
      }

      updatedRefCodes['brand'] = formModel.eventProperty.brand;
      updatedRefCodes['chain'] = formModel.eventProperty.chain;
      updatedRefCodes['propertyId'] = formModel.eventProperty.propertyId;
    }

    updatedRefCodes['startDate'] = new Date(formModel.startDate).toISOString();
    updatedRefCodes['endDate'] = new Date(formModel.endDate).toISOString();

    // checks if the user did not click the add button
    if (model.event.ratePlanCode) {
      formModel.ratePlanCodes.push(
        new RatePlanCode({ ratePlanCode: model.event.ratePlanCode })
      );
    }

    this.logService.debug(`updatedRefCodes ${updatedRefCodes}`);
    const updatedModel = formModel.merge(updatedRefCodes) as OnyxEvent;

    let obs;
    if (this.authenticationStore.isPropertyUser()) {
      obs = this.sendAddEventRequest(updatedModel);
    } else {
      obs = this.sendAddAgencyEventRequest(updatedModel);
    }
    obs.subscribe();
    return obs;
  }

  sendAddEventRequest(originalEvent: OnyxEvent): Observable<OnyxEvent> {
    let returnedEvent = this.eventStore
      .addEvent(originalEvent)
      .catch((error) => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER',
            error
          );
        } else if (
          error.includes('Property Event Reference Number already exists')
        ) {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER',
            error
          );
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT', error);
        }
        return Observable.throw(error);
      })
      .map((res) => {
        return this.refreshAfterAdding(res);
      });
    return returnedEvent;
  }

  sendAddAgencyEventRequest(originalEvent: OnyxEvent): Observable<OnyxEvent> {
    let returnedEvent = this.eventStore
      .addAgencyEvent(originalEvent)
      .catch((error) => {
        this.eventForm.enable();
        if (error.includes('Agency Event Reference Number already exists')) {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.ADD_EVENT_DUPLICATED_AGENCY_REFERENCE_NUMBER',
            error
          );
        } else if (
          error.includes('Property Event Reference Number already exists')
        ) {
          this.flashMessagesService.error(
            'MESSAGES.ERROR.ADD_EVENT_DUPLICATED_PROPERTY_REFERENCE_NUMBER',
            error
          );
        } else {
          this.flashMessagesService.error('MESSAGES.ERROR.ADD_EVENT', error);
        }
        return Observable.throw(error);
      })
      .map((res) => {
        return this.refreshAfterAdding(res);
      });
    return returnedEvent;
  }

  private refreshAfterAdding(res: OnyxEvent) {
    let changeIata = false;
    if (
      this.user &&
      this.user.allArcIatas &&
      this.user.customerInfo &&
      this.user.customerInfo.length >= 2
    ) {
      const key =
        this.user.username.toUpperCase() +
        AuthenticationStore.CURRENT_AGENCY_STR;
      const currentAgency = JSON.parse(
        this.userLocalStorageService.getItem(key)
      );
      if (currentAgency.agencyId !== res.agencyId) {
        changeIata = true;
      }
    }
    if (changeIata) {
      const agency: MultiAgency = new MultiAgency({
        agencyId: res.agencyId,
        agencyName: res.eventAgency.name,
      });
      this.authenticationStore
        .updateIataFromToken(agency.agencyId)
        .subscribe(() => {
          this.multiAgencyService.setAgencySelectedUI(true);
          this.multiAgencyService.setDefaultAgency(agency);
          this.goToContractSummary(res.eventId);
          return res;
        });
    } else {
      this.goToContractSummary(res.eventId);
      return res;
    }
  }

  returnToSearchList(): void {
    this.eventSearchParametersStore.updateUrl();
  }

  private goToContractSummary(eventId: number) {
    this.router.navigate(['/event', eventId, 'summary']);
  }

  setupPropertyEmail(email: string): void {
    this.propertyEmail = email;
  }

  setupAgencyEmail(email: string): void {
    this.agencyEmail = email;
  }

  public confirmBeforeSaveEventHandler(proceed: boolean) {
    this.eventForm.disable();
    if (proceed) {
      this.goToContractSummary(this.eventMatchId);
    } else {
      this.save(this.modelInCollision);
    }
  }

  eventCollision(model: any): void {
    let firstName = '';
    let lastName = '';
    let email = '';
    let phone = '';
    let fax = '';
    let createParty = '';
    let eventReferenceNumber = model.eventProperty.eventReferenceNumber;
    let autoGenerated = '';
    let manuallyAdded = '';
    if (this.authenticationStore.isPropertyUser()) {
      firstName = model.eventProperty.contact.firstName;
      lastName = model.eventProperty.contact.lastName;
      email = model.eventProperty.contact.email;
      phone = model.eventProperty.contact.phone;
      fax = model.eventProperty.contact.fax;
      createParty = model.eventProperty.contact.partyType;
      autoGenerated = model.eventProperty.autoGenerated ? 'Y' : 'N';
      manuallyAdded = model.eventProperty.manuallyAdded ? 'Y' : 'N';
    } else {
      firstName = model.eventAgency.contact.firstName;
      lastName = model.eventAgency.contact.lastName;
      email = model.eventAgency.contact.email;
      phone = model.eventAgency.contact.phone;
      fax = model.eventAgency.contact.fax;
      createParty = model.eventAgency.contact.partyType;
      autoGenerated = model.eventAgency.autoGenerated ? 'Y' : 'N';
      manuallyAdded = model.eventAgency.manuallyAdded ? 'Y' : 'N';
    }
    this.modelInCollision = model;
    model.event.eventAgency = model.eventAgency;
    model.event.eventClient = model.eventClient;
    model.event.eventProperty = model.eventProperty;
    const formModel = new OnyxEvent(model.event);
    const eventData: EventCollisionInfo = new EventCollisionInfo({
      create_party: createParty,
      event_name: formModel.name,
      start_date: formModel.startDate,
      end_date: formModel.endDate,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone: phone,
      fax: fax,
      chain: formModel.eventProperty.chain,
      brand: formModel.eventProperty.brand,
      property_address: `${formModel.eventProperty.line1} ${formModel.eventProperty.line2}`,
      property_zip: formModel.eventProperty.postalCode,
      property_city: formModel.eventProperty.city,
      property_state: formModel.eventProperty.state,
      property_country: formModel.eventProperty.countryCode,
      name: formModel.eventAgency.name,
      dba_name: formModel.eventAgency.dbaName,
      line1: formModel.eventAgency.line1,
      line2: formModel.eventAgency.line2,
      line3: formModel.eventAgency.line3,
      city: formModel.eventAgency.city,
      state: formModel.eventAgency.state,
      postal_code: formModel.eventAgency.postalCode,
      country_code: formModel.eventAgency.countryCode,
      event_reference_number: eventReferenceNumber,
      auto_generated_code: autoGenerated,
      manually_added: manuallyAdded,
    });
    const eventInfo = [];
    eventInfo.push(eventData);
    const resp = this.matchingPropertyStore
      .getMatchingCollisionProperties(eventInfo)
      .subscribe((match) => {
        if (match) {
          if (match[0].result === 'exact' || match[0].result === 'fuzzy') {
            const eventMatch = new EventCollisionMatch({
              createdBy: formModel.eventAgency.name,
              agencyName: formModel.eventAgency.dbaName,
              eventName: formModel.name,
              groupCode: formModel.groupCodeNumber,
              startDate: formModel.startDate,
              endDate: formModel.endDate,
              property: `${formModel.eventProperty.name} ${formModel.eventProperty.line1}`,
            });
            this.eventMatchId = match[0].matches[0].event_id;
            this.eventCollisionModal.open(eventMatch, match[0].matches[0]);
          } else {
            this.save(model);
          }
        }
      });
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/add-event/add-event.component.ts